// =============================================================
// 💎 App.tsx — Version finale alignée avec AuthContext Platinum PRO
// =============================================================

import React, { Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./toastify-overrides.css";
import { useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import OfflineGate from "./components/pwa/OfflineGate";

import "./index.css";

// 🔐 Lazy routes (voir routes/lazyRoutes.tsx)
import * as R from "./routes/lazyRoutes";
import LoginPage from "./pages/LoginPage";
import AdminRouteGuard from "./modules/admin/layout/AdminRouteGuard";
import ClientRouteGuard from "./modules/client-portal/layout/ClientRouteGuard";
import { ADMIN_CLIENT_ROUTE_PATHS, ADMIN_MIRRORED_ROUTES } from "./routes/adminChildRoutes";

import { getDefaultRouteForUser } from "./utils/roleAccess";

// =============================================================
// 🚀 App principale
// =============================================================

/** Anciennes URLs `/clients`, `/clients/nouveau`, `/clients/reclamations`… → `/admin/clients…`. */
function LegacyClientsPathRedirect(): React.JSX.Element {
  const { pathname } = useLocation();
  const suffix = pathname.slice("/clients".length);
  return <Navigate to={`/admin/clients${suffix}`} replace />;
}

/** Anciennes URLs finance / recouvrement (hors /admin) → chemins canoniques sous AdminRouteGuard. */
function LegacyFinanceFacturesRedirect(): React.JSX.Element {
  const { pathname } = useLocation();
  const rest = pathname.replace(/^\/finance\/factures/, "");
  return <Navigate to={`/admin/finance/factures${rest || ""}`} replace />;
}

function RoleBasedHomeRedirect(): React.JSX.Element {
  const { user, isLoading } = useAuth();

  if (isLoading) return <Navigate to="/login" replace />;
  if (!user) return <Navigate to="/login" replace />;

  return <Navigate to={getDefaultRouteForUser(user)} replace />;
}

function AppRoutes(): React.JSX.Element {

  return (
    <Routes>
        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-questions" element={<R.ResetQuestions />} />
        <Route path="/set-password" element={<R.SetPassword />} />
        <Route path="/client/login" element={<LoginPage />} />

        <Route path="/admin" element={<AdminRouteGuard><R.AdminMetierShell /></AdminRouteGuard>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<R.DashboardHome />} />
          <Route path="agents/nouveau" element={<R.AdminAgentsNouveauRedirect />} />
          <Route path="agents" element={<R.AgentsAdminLayout />}>
            <Route index element={null} />
            <Route path=":id" element={<R.AgentTerrainDetailModal />} />
          </Route>
          <Route path="utilisateurs/nouveau" element={<R.AdminUserCreatePage />} />
          <Route path="utilisateurs/:id" element={<R.AdminUserDetailPage />} />
          <Route path="utilisateurs" element={<R.AdminUsersPage />} />
          <Route path="clients" element={<R.AdminClientsLayout />}>
            <Route index element={<R.ClientsPage />} />
            <Route path="reclamations" element={<R.ReclamationsCitoyennes />} />
            <Route path="nouveau" element={<R.ClientNouveauPage />} />
            <Route path=":id" element={<R.FicheClient />} />
          </Route>
          {ADMIN_MIRRORED_ROUTES.filter((r) => !ADMIN_CLIENT_ROUTE_PATHS.has(r.path)).map(
            ({ path, Element }) => (
              <Route key={path} path={path} element={<Element />} />
            )
          )}
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Route>

        <Route
          path="/client/*"
          element={
            <ClientRouteGuard>
              <R.ClientShell />
            </ClientRouteGuard>
          }
        >
          <Route index element={<Navigate to="/client/me/dashboard" replace />} />
          <Route path="dashboard" element={<Navigate to="/client/me/dashboard" replace />} />
          <Route path="me" element={<Navigate to="/client/me/dashboard" replace />} />
          <Route path="me/dashboard" element={<R.ClientDashboardPage />} />
          <Route path="me/abonnement" element={<R.ClientAbonnementPage />} />
          <Route path="me/passages" element={<R.ClientPassagesPage />} />
          <Route path="me/factures" element={<R.ClientFacturesPage />} />
          <Route path="me/provision" element={<R.ClientProvisionPage />} />
          <Route path="me/paiements" element={<R.ClientPaiementsPage />} />
          <Route path="me/paiements/preuve" element={<R.ClientPaiementPreuvePage />} />
          <Route path="me/paiements/historique" element={<R.ClientPaiementsHistoriquePage />} />
          <Route path="me/reclamations" element={<R.ClientReclamationsPage />} />
          <Route path="me/documents" element={<R.ClientDocumentsPage />} />
          <Route path="me/documents-financiers" element={<R.ClientDocumentsPage />} />
          <Route path="me/profil" element={<R.ClientProfilPage />} />
          <Route path="me/demandes" element={<R.ClientDemandesPage />} />
          <Route path="me/planning" element={<R.ClientPlanningPage />} />
          <Route path="me/prestations" element={<R.ClientPrestationsPage />} />
          <Route path="me/localisation" element={<R.ClientLocalisationPage />} />
          <Route path="me/quittances" element={<R.ClientQuittancesPage />} />
          <Route
            path="abonnements"
            element={<Navigate to="/client/me/abonnement" replace />}
          />
          <Route path="abonnement" element={<Navigate to="/client/me/abonnement" replace />} />
          <Route path="passages" element={<Navigate to="/client/me/passages" replace />} />
          <Route path="factures" element={<Navigate to="/client/me/factures" replace />} />
          <Route path="provision" element={<Navigate to="/client/me/provision" replace />} />
          <Route path="paiements" element={<Navigate to="/client/me/paiements" replace />} />
          <Route path="reclamations" element={<Navigate to="/client/me/reclamations" replace />} />
          <Route path="profil" element={<Navigate to="/client/me/profil" replace />} />
          <Route path="demandes" element={<Navigate to="/client/me/demandes" replace />} />
          <Route path="planning" element={<Navigate to="/client/me/planning" replace />} />
          <Route path="prestations" element={<Navigate to="/client/me/prestations" replace />} />
          <Route path="localisation" element={<Navigate to="/client/me/localisation" replace />} />
          <Route path="quittances" element={<Navigate to="/client/me/quittances" replace />} />
          <Route path="*" element={<Navigate to="/client/me/dashboard" replace />} />
        </Route>

        <Route path="/ag/:agentId" element={<R.AgentShell />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<R.AgentDashboardPage />} />
          <Route path="tournee" element={<R.AgentTourneePage />} />
          <Route path="tournees/:id" element={<R.AgentTourneeDetailPage />} />
          <Route path="interventions" element={<R.AgentInterventionsPage />} />
          <Route path="historique" element={<R.AgentHistoriquePage />} />
          <Route path="incidents" element={<R.AgentIncidentsPage />} />
          <Route path="passages/:id" element={<R.AgentPassageDetailPage />} />
        </Route>
        <Route path="/ag/me" element={<Navigate to="/ag/me/dashboard" replace />} />
        <Route path="/agent" element={<Navigate to="/ag/me/dashboard" replace />} />
        <Route path="/agent/tournee" element={<Navigate to="/ag/me/tournee" replace />} />
        <Route path="/agent/passages/:id" element={<Navigate to="/ag/me/dashboard" replace />} />
        
        {/* Routes protégées sous Layout */}
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<RoleBasedHomeRedirect />} />

          {/* === Toutes les routes métiers === */}
          {/* Charroi */}
          <Route path="/charroi" element={<R.ListeVehicules />} />
          <Route path="/charroi/dashboard" element={<R.CharroiDashboard />} />
          <Route path="/charroi/vehicules" element={<R.ListeVehicules />} />
{/* ✅ Correction ici : on ne passe plus onBack */}
<Route
  path="/charroi/vehicules/nouveau"
  element={<R.VehiculeForm />}
/>
          <Route path="/charroi/vehicule/:id/edit" element={<R.VehiculeEditPage />} />
          <Route path="/charroi/maintenances" element={<R.MaintenancePage />} />
          <Route
            path="/charroi/maintenance/nouveau"
            element={<R.MaintenanceForm onBack={() => window.history.back()} />}
          />
          <Route
            path="/charroi/maintenance/liste"
            element={<R.MaintenanceVehicules />}
          />
<Route
  path="/charroi/vehicule/:id"
  element={<R.VehiculeDetailPage />}
/>
          <Route path="/charroi/alertes" element={<R.CharroiAlertes />} />
          <Route path="/charroi/stats" element={<R.CharroiStats />} />

          {/* Dépôts */}
          <Route path="/depots" element={<R.ListeDepots />} />
          <Route path="/depots/nouveau" element={<R.NewDepot />} />
          <Route path="/depots/:id" element={<R.EditDepot />} />
          <Route path="/depots/:id/view" element={<R.ViewDepot />} />

          {/* Paramètres / Admin */}
          <Route path="/parametres" element={<R.ParametresPage />} />
          <Route path="/utilisateurs" element={<R.AdminUsersPage />} />
          <Route path="/utilisateurs/nouveau" element={<R.NouvelUtilisateur />} />
          <Route path="/utilisateurs/:id/edit" element={<R.EditUtilisateur />} />
          <Route path="/configuration" element={<R.AdminConfigPage />} />
          <Route path="/zones" element={<Navigate to="/admin/zones" replace />} />
          <Route path="/societes" element={<Navigate to="/admin/parametres" replace />} />
          <Route path="/configuration/societes" element={<Navigate to="/admin/parametres" replace />} />
          <Route path="/parts" element={<Navigate to="/admin/parametres" replace />} />
          <Route path="/admin/dashboard" element={<R.EspoirDashboard />} />
          <Route path="/logs" element={<R.HistoriqueActions />} />
          <Route path="/automatisation" element={<R.Automatisation />} />
          <Route path="/automatisations" element={<R.ListeAutomatisations />} />
          <Route path="/automatisations/new" element={<R.NewAutomatisation />} />
          <Route
            path="/automatisations/edit/:id"
            element={<R.EditAutomatisation />}
          />
          <Route
            path="/automatisations/dashboard"
            element={<R.DashboardAutomatisations />}
          />

          {/* Agents */}
          <Route path="/agents" element={<R.ListeAgentsRH />} />
          <Route path="/agents/nouveau" element={<R.NouvelAgent />} />
          <Route path="/agents/:id" element={<R.AgentLegacyFicheEntry />} />
          <Route path="/agents/:id/carte" element={<R.CarteAgent />} />
          <Route path="/agents/dashboard" element={<R.AgentsDashboard />} />

          {/* Clients : entrée unique sous /admin/clients ; anciennes routes redirigées */}
          <Route path="/clients/*" element={<LegacyClientsPathRedirect />} />
          <Route path="/client/dashboard" element={<R.ClientDashboard />} />
          <Route path="/contrats" element={<R.ContratsPage />} />

          {/* Chef */}
          <Route path="/chef" element={<R.ChefDashboard />} />
          <Route path="/chef/missions" element={<R.MissionsDuJour />} />
          <Route path="/chef/mission/:id" element={<R.MissionDetailsChef />} />
          <Route
            path="/chef/mission/:id/finale"
            element={<R.MissionFinaleChef />}
          />
          <Route path="/chef/messages" element={<R.MessagesTerrain />} />
          <Route path="/chef/inventaire" element={<R.InventaireTerrain />} />
          <Route path="/chef/depots" element={<R.DepotsPage />} />
          <Route path="/interventions" element={<R.InterventionsPage />} />
          <Route
            path="/chef/liste-interventions"
            element={<R.ListeInterventions />}
          />
          <Route path="/chef/liste-missions" element={<R.ListeMissions />} />
<Route path="/missions/nouvelle" element={<R.NouvelleMission />} />
<Route path="/paiement/success" element={<R.PaiementSuccess />} />

          {/* Planificateur : URLs canoniques sous /admin/planificateur — redirections depuis /planificateur */}
          <Route path="/planificateur/*" element={<R.RedirectToAdminPlanificateur />} />
          <Route path="/passages/urgence" element={<R.PassageUrgencePage />} />
 
          {/* Mairie */}
          <Route path="/mairie/dashboard" element={<R.DashboardMairie />} />
          <Route path="/mairie/stats" element={<R.StatsMairie />} />
          <Route path="/mairie/export" element={<R.ExportMairie />} />
          <Route path="/mairie/alertes" element={<R.AlertesMairie />} />
          <Route path="/mairie/historique" element={<R.MairieHistorique />} />
          <Route path="/mairie/mes-notes" element={<R.MesNotes />} />
          <Route path="/mairie/notes-a-payer" element={<R.NotesAPayer />} />

          {/* Superviseur terrain */}
          <Route path="/superviseur" element={<R.SuperviseurDashboard />} />
          <Route
            path="/superviseur/missions"
            element={
              <R.MetierPlaceholderPage
                title="Missions (vue liste superviseur)"
                description="Cette vue liste sera reliée au flux missions terrain. Utilisez le tableau de bord pour l’aperçu opérationnel."
                primaryLink={{ to: "/superviseur", label: "Dashboard superviseur" }}
              />
            }
          />
          <Route path="/superviseur/mission/:id" element={<R.SuperviseurMission />} />
          <Route path="/superviseur/stats" element={<R.StatsSuperviseur />} />
          <Route path="/superviseur/alertes" element={<R.SignalementSuperviseur />} />
 
{/* Sécurité Physique / Portier */}
<Route path="/portier/dashboard" element={<R.PortierDashboard />} />
<Route path="/portier/scanner" element={<R.PortierScanner />} />
<Route path="/portier/historique" element={<R.PortierHistorique />} />
<Route path="/portier/archives" element={<R.PortierArchives />} />

          {/* Recouvrement */}
          <Route path="/recouvrement/liste" element={<R.ListePaiements />} />
          <Route path="/recouvrement" element={<Navigate to="/admin/recouvrement" replace />} />
          <Route path="/recouvrement/ajouter" element={<Navigate to="/admin/finance/recouvrement" replace />} />
          <Route
            path="/recouvrement/:id/modifier"
            element={<R.EditPaiement />}
          />
          <Route path="/recouvrement/stats" element={<R.StatsRecouvrement />} />
          <Route
            path="/recouvrement/stats-zones"
            element={<R.StatsZones />}
          />
          <Route
            path="/recouvrement/journal"
            element={<R.JournalModifications />}
          />
          <Route path="/paiements" element={<Navigate to="/admin/paiements" replace />} />

{/* === Proformas (legacy) → admin === */}
<Route path="/recouvrement/proformas/nouvelle" element={<Navigate to="/admin/finance/proformas/nouvelle" replace />} />
<Route path="/recouvrement/proformas" element={<Navigate to="/admin/finance/proformas" replace />} />

<Route
  path="/recouvrement/historique"
  element={<R.HistoriqueRecouvrement />}
/>

          {/* Clôtures */}
          <Route path="/clotures" element={<R.ListeClotures />} />
          <Route
            path="/clotures/historique"
            element={<R.HistoriqueClotures />}
          />
  
          {/* Finance */}
          <Route path="/finance/dashboard" element={<Navigate to="/finance" replace />} />
          <Route path="/finance" element={<R.DashboardFinance />} />
          <Route path="/finance/audit" element={<R.AuditPage />} />
          <Route path="/finance/journal" element={<R.JournalActivite />} />
          <Route path="/finance/alertes" element={<R.AlertesFinance />} />
          <Route path="/finance/factures/*" element={<LegacyFinanceFacturesRedirect />} />
          <Route path="/finance/recouvrement" element={<R.RecouvrementClient />} />
          <Route path="/finance/paiements" element={<R.PaiementsParMode />} />
          <Route path="/finance/previsions" element={<R.PrevisionsFinance />} />
          <Route path="/finance/synthese" element={<R.SyntheseMensuelle />} />
          {/* Dashboards globaux */}
          <Route
            path="/dashboard/terrain"
            element={<R.TerrainSupervisionDashboard />}
          />
          <Route
            path="/dashboard/planification"
            element={<R.PlanificationLogistiqueDashboard />}
          />
          <Route
            path="/dashboard/finance"
            element={<R.DashboardFinanceGlobal />}
          />
          <Route
            path="/dashboard/admin-securite"
            element={<R.AdminSecuriteDashboard />}
          />

          {/* Autres */}
          <Route path="/map/:zone" element={<R.CarteZonePage />} />
          <Route
            path="/terrain/chef/intervention/:id"
            element={<R.InterventionChefStubPage />}
          />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
  );
}

function App() {
  return (
    <>
      <ToastContainer
        position="top-right"
        theme="dark"
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
        draggable={false}
        limit={5}
      />
      <OfflineGate>
        <AppRoutes />
      </OfflineGate>
    </>
  );
}
  
export default App; 